import { Pressable, Text, View } from "react-native";
import { ChevronRight, MapPin, Plus } from "lucide-react-native";
import {
  CardSubtitleText,
  CardSurface,
  CardTitleText,
  CompactActionButton,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  StatusBadge,
  Tag,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import { useTranslations, useLocale } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n/translations";
import {
  getRemainingOocyteCohortCount,
  useFrozenSpecimens,
  type OocyteCohort,
  type Specimen,
} from "./useSpecimens";
import { isInFrozenStorage } from "./frozenSpecimenAvailability";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const TEXT = EMBER.colors.text;
const TEXT_LIGHT = EMBER.colors.textLight;

export type CryoSpecimenSnapshot = {
  id: string;
  label: string;
  count: string;
  facility: string;
  renewal: string;
  detail: string;
  status: string;
  organicIcon: OrganicMiniIconKind;
  specimenIds: string[];
};

export type CryoVaultCarouselItem = {
  kind: "specimen";
  specimen: CryoSpecimenSnapshot;
};

function formatShortDate(date: string | null | undefined, locale: string) {
  if (!date) return "";
  const parsed = new Date(`${date.split("T")[0]}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
}

export function buildCryoSpecimenSnapshotsFromStorageGroups(
  t: TranslationFunction,
  locale: string,
  storageGroups: ReturnType<typeof useFrozenSpecimens>["storageGroups"],
  cohorts: readonly OocyteCohort[] = [],
): CryoSpecimenSnapshot[] {
  const cohortByMemberId = new Map<string, OocyteCohort>();
  for (const cohort of cohorts) {
    for (const member of cohort.members) {
      cohortByMemberId.set(member.id, cohort);
    }
  }

  const snapshots: CryoSpecimenSnapshot[] = [];
  const cohortSnapshots = new Map<string, CryoSpecimenSnapshot>();

  for (const [index, group] of storageGroups.entries()) {
    const cohort =
      group.type === "oocyte"
        ? group.specimens
            .map((specimen) => cohortByMemberId.get(specimen.id))
            .find((candidate): candidate is OocyteCohort => candidate != null)
        : undefined;

    if (cohort) {
      const count = getRemainingOocyteCohortCount(
        cohort.numberFrozen,
        cohort.numberThawed,
        cohort.members.length,
      );
      const existing = cohortSnapshots.get(cohort.id);
      if (existing) {
        existing.specimenIds = [
          ...new Set([...existing.specimenIds, ...group.specimenIds]),
        ];
        continue;
      }

      const typeLabel =
        count === 1
          ? t("care.cryo_vault.overview.type_egg_singular")
          : t("care.cryo_vault.overview.type_egg_plural");
      const storedDate = formatShortDate(group.storedDate, locale);
      const collectedDate = formatShortDate(group.collectedAt, locale);
      const snapshot: CryoSpecimenSnapshot = {
        id: `oocyte-cohort-${cohort.id}`,
        label: typeLabel,
        count: String(count),
        facility: group.facility,
        renewal: t("care.cryo_vault.overview.renewal_not_set"),
        detail: storedDate
          ? t("care.cryo_vault.overview.detail_stored_since", {
              params: { date: storedDate },
            })
          : collectedDate
            ? t("care.cryo_vault.overview.detail_collected", {
                params: { date: collectedDate },
              })
            : t("care.cryo_vault.overview.detail_default"),
        status: t("care.cryo_vault.overview.status_stored"),
        organicIcon: "eggs",
        specimenIds: [...group.specimenIds],
      };
      cohortSnapshots.set(cohort.id, snapshot);
      continue;
    }

    const count = group.specimens.length;
    const typeLabel =
      group.type === "oocyte"
        ? count === 1
          ? t("care.cryo_vault.overview.type_egg_singular")
          : t("care.cryo_vault.overview.type_egg_plural")
        : group.type === "embryo"
          ? count === 1
            ? t("care.cryo_vault.overview.type_embryo_singular")
            : t("care.cryo_vault.overview.type_embryo_plural")
          : count === 1
            ? t("care.cryo_vault.overview.type_sperm_singular")
            : t("care.cryo_vault.overview.type_sperm_plural");
    const storedDate = formatShortDate(group.storedDate, locale);
    const collectedDate = formatShortDate(group.collectedAt, locale);

    snapshots.push({
      id: `${group.type}-${group.facility}-${index}`,
      label: typeLabel,
      count: String(count),
      facility: group.facility,
      renewal: t("care.cryo_vault.overview.renewal_not_set"),
      detail: storedDate
        ? t("care.cryo_vault.overview.detail_stored_since", {
            params: { date: storedDate },
          })
        : collectedDate
          ? t("care.cryo_vault.overview.detail_collected", {
              params: { date: collectedDate },
            })
          : t("care.cryo_vault.overview.detail_default"),
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon:
        group.type === "oocyte"
          ? "eggs"
          : group.type === "embryo"
            ? "genetics"
            : "sperm",
      specimenIds: group.specimenIds,
    });
  }

  return [...snapshots, ...cohortSnapshots.values()];
}

/**
 * Snapshot builder that mirrors the CryoVault screen's own selection: every
 * specimen the API returns, minus those already represented by an oocyte
 * cohort, plus one snapshot per cohort. Applies the same `isInFrozenStorage`
 * rule the vault uses so the two surfaces cannot silently disagree on what is
 * in her vault — a specimen still in `processed` state, or a cohort fully
 * thawed out, is not something she can walk into a clinic and use.
 */
export function buildCryoSpecimenSnapshotsFromApiSpecimens(
  t: TranslationFunction,
  locale: string,
  apiSpecimens: readonly Specimen[],
  cohorts: readonly OocyteCohort[] = [],
): CryoSpecimenSnapshot[] {
  const cohortMemberIds = new Set(
    cohorts.flatMap((cohort) => cohort.members.map((member) => member.id)),
  );
  const individualSpecimens = apiSpecimens.filter(
    (specimen) =>
      !cohortMemberIds.has(specimen.id) && isInFrozenStorage(specimen),
  );
  const vaultCohorts = cohorts.filter(
    (cohort) =>
      cohort.status === "stored" || cohort.status === "partially_used",
  );

  const individualSnapshots: CryoSpecimenSnapshot[] = individualSpecimens.map(
    (specimen) => {
      const activeLocation =
        specimen.storageLocations.find((loc) => loc.isActive) ??
        specimen.storageLocations[0];
      const facility = activeLocation?.facility ?? "";
      const storedFrom = activeLocation?.storedFrom ?? null;
      const storedDate = formatShortDate(storedFrom, locale);
      const collectedDate = formatShortDate(specimen.collectedAt, locale);
      const label =
        specimen.type === "oocyte"
          ? t("care.cryo_vault.overview.type_egg_singular")
          : specimen.type === "embryo"
            ? t("care.cryo_vault.overview.type_embryo_singular")
            : t("care.cryo_vault.overview.type_sperm_singular");
      const organicIcon: OrganicMiniIconKind =
        specimen.type === "oocyte"
          ? "eggs"
          : specimen.type === "embryo"
            ? "genetics"
            : "sperm";
      return {
        id: `specimen-${specimen.id}`,
        label,
        count: "1",
        facility,
        renewal: t("care.cryo_vault.overview.renewal_not_set"),
        detail: storedDate
          ? t("care.cryo_vault.overview.detail_stored_since", {
              params: { date: storedDate },
            })
          : collectedDate
            ? t("care.cryo_vault.overview.detail_collected", {
                params: { date: collectedDate },
              })
            : t("care.cryo_vault.overview.detail_default"),
        status: t("care.cryo_vault.overview.status_stored"),
        organicIcon,
        specimenIds: [specimen.id],
      };
    },
  );

  const cohortSnapshots: CryoSpecimenSnapshot[] = vaultCohorts.map((cohort) => {
    const count = getRemainingOocyteCohortCount(
      cohort.numberFrozen,
      cohort.numberThawed,
      cohort.members.length,
    );
    const typeLabel =
      count === 1
        ? t("care.cryo_vault.overview.type_egg_singular")
        : t("care.cryo_vault.overview.type_egg_plural");
    const activeMember = cohort.members.find(
      (member) => member.storageFacility || member.frozenDate,
    );
    const facility = activeMember?.storageFacility ?? "";
    const frozenDate = activeMember?.frozenDate ?? null;
    const storedDate = formatShortDate(frozenDate, locale);
    return {
      id: `oocyte-cohort-${cohort.id}`,
      label: typeLabel,
      count: String(count),
      facility,
      renewal: t("care.cryo_vault.overview.renewal_not_set"),
      detail: storedDate
        ? t("care.cryo_vault.overview.detail_stored_since", {
            params: { date: storedDate },
          })
        : t("care.cryo_vault.overview.detail_default"),
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "eggs",
      specimenIds: cohort.members.map((member) => member.id),
    };
  });

  return [...individualSnapshots, ...cohortSnapshots];
}

export function CryoSpecimenCarouselCard({
  specimen,
  width,
  equalizedHeight,
  onPress,
}: {
  specimen: CryoSpecimenSnapshot;
  width: number;
  equalizedHeight?: number;
  onPress?: () => void;
}) {
  const t = useTranslations();
  /**
   * Her eggs are somewhere even when she is not sure where.
   *
   * A placement can now be recorded without naming the facility — she may know
   * they are frozen and not be certain which centre holds them — so this line
   * had to stop rendering an empty string. Same fallback the Cryo Vault screen
   * already uses for a nameless group, rather than a second wording for one
   * state.
   */
  const facilityLabel =
    specimen.facility || t("cryo_vault.grouped_inventory.unknown_facility");
  const capturePress = usePressCapture("CryoSpecimenCarouselCard");
  const storedStatus = t("care.cryo_vault.overview.status_stored");
  return (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(`${specimen.count} ${specimen.label}`),
          "specimen_card",
        );
        onPress?.();
      }}
      disabled={!onPress}
      accessibilityRole={onPress ? "button" : undefined}
      accessibilityLabel={`${specimen.count} ${specimen.label}, ${facilityLabel}`}
      style={({ pressed }) => ({
        ...cardSurface({ variant: "panel" }),
        width,
        minHeight: equalizedHeight,
        padding: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: pressed && onPress ? 0.74 : 1,
      })}
    >
      <OrganicMiniIcon kind={specimen.organicIcon} size={32} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {specimen.count} {specimen.label}
        </Text>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginTop: 2,
          }}
        >
          <MapPin size={11} color={TEXT_LIGHT} strokeWidth={1.8} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              fontSize: 10.5,
              lineHeight: 13,
              color: TEXT_LIGHT,
              fontWeight: "700",
              flex: 1,
            }}
          >
            {facilityLabel}
          </Text>
        </View>
      </View>
      {specimen.status !== storedStatus ? (
        <StatusBadge label={specimen.status} />
      ) : null}
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.7} />
    </Pressable>
  );
}

export function CryoVaultEmptyState({
  onAddSpecimen,
  onNoSpecimens,
}: {
  onAddSpecimen: () => void;
  onNoSpecimens: () => void;
}) {
  const t = useTranslations();
  const specimenTypes = [
    t("care.cryo_vault.overview.empty_chip_eggs"),
    t("care.cryo_vault.overview.empty_chip_embryos"),
    t("care.cryo_vault.overview.empty_chip_sperm"),
    t("care.cryo_vault.overview.empty_chip_facility"),
    t("care.cryo_vault.overview.empty_chip_reports"),
  ];

  return (
    <CardSurface variant="prompt" style={{ padding: 14, gap: 12 }}>
      <View style={{ gap: 4 }}>
        <EmptyStateTitleText>
          {t("care.cryo_vault.overview.any_frozen_specimens_to_add")}
        </EmptyStateTitleText>
        <EmptyStateSubtitleText style={{ marginTop: 0 }}>
          {t("care.cryo_vault.overview.add_specimens_or_mark_not_applicable")}
        </EmptyStateSubtitleText>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
        {specimenTypes.map((type) => (
          <Tag key={type} label={type} variant="outline" shape="pill" />
        ))}
      </View>

      <View style={{ flexDirection: "row", gap: 9 }}>
        <CompactActionButton
          label={t("care.cryo_vault.overview.empty_none_button")}
          action="care.cryo_vault.overview.empty_none_button"
          accessibilityLabel={t("care.cryo_vault.overview.empty_none_a11y")}
          variant="secondary"
          size="md"
          flexGrow={1}
          flexBasis={0}
          onPress={onNoSpecimens}
        />
        <CompactActionButton
          label={t("care.cryo_vault.overview.empty_add_button")}
          action="care.cryo_vault.overview.empty_add_button"
          accessibilityLabel={t("care.cryo_vault.overview.empty_add_a11y")}
          variant="primary"
          size="md"
          leadingIcon={Plus}
          flexGrow={1}
          flexBasis={0}
          onPress={onAddSpecimen}
        />
      </View>
    </CardSurface>
  );
}

function buildFixtureCryoSpecimenSnapshots(
  t: TranslationFunction,
): CryoSpecimenSnapshot[] {
  return [
    {
      id: "stored-eggs",
      label: t("care.cryo_vault.overview.type_egg_plural"),
      count: "11",
      facility: "NY Cryo",
      renewal: "May 2027",
      detail: "Egg freezing round, Jan 2025",
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "eggs",
      specimenIds: [],
    },
    {
      id: "stored-embryos",
      label: t("care.cryo_vault.overview.type_embryo_plural"),
      count: "4",
      facility: "Future Fertility Lab",
      renewal: "Aug 2026",
      detail: "PGT-A report expected after biopsy results",
      status: t("care.cryo_vault.overview.status_report_pending"),
      organicIcon: "genetics",
      specimenIds: [],
    },
    {
      id: "sperm-backup",
      label: t("care.cryo_vault.overview.type_sperm_plural"),
      count: "3",
      facility: "CryoBank West",
      renewal: "Mar 2027",
      detail: "Partner specimen linked to this plan",
      status: t("care.cryo_vault.overview.status_stored"),
      organicIcon: "sperm",
      specimenIds: [],
    },
  ];
}

export function CryoVaultOverviewCard({
  onPress,
  onAddSpecimen,
  onNoSpecimens,
  onPressSpecimen,
  specimens,
}: {
  onPress: () => void;
  onAddSpecimen: () => void;
  onNoSpecimens: () => void;
  onPressSpecimen?: (specimen: CryoSpecimenSnapshot) => void;
  specimens?: CryoSpecimenSnapshot[];
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CryoVaultOverviewCard");
  const effectiveSpecimens = specimens ?? buildFixtureCryoSpecimenSnapshots(t);
  const hasSpecimens = effectiveSpecimens.length > 0;
  /**
   * Only named centres are counted.
   *
   * A placement can now be recorded without naming the facility, which arrives
   * here as `""` — and an unnamed one joined the set as a centre of its own, so
   * eggs at one known clinic plus eggs whose centre she could not name read as
   * "across 2 facilities", directly above a card correctly showing the second
   * as unnamed.
   */
  const facilityCount = new Set(
    effectiveSpecimens
      .map((specimen) => specimen.facility.trim())
      .filter((facility) => facility.length > 0),
  ).size;
  const totalSpecimens = effectiveSpecimens.reduce(
    (sum, specimen) => sum + (parseInt(specimen.count, 10) || 0),
    0,
  );
  const carouselItems: CryoVaultCarouselItem[] = effectiveSpecimens.map(
    (specimen) => ({
      kind: "specimen" as const,
      specimen,
    }),
  );

  return (
    <CardSurface style={{ padding: 15, gap: 13 }}>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("care.cryo_vault.overview.open_a11y")),
            "open_cryo_vault",
          );
          onPress();
        }}
        accessibilityRole="button"
        accessibilityLabel={t("care.cryo_vault.overview.open_a11y")}
        style={({ pressed }) => ({
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          opacity: pressed ? 0.74 : 1,
        })}
      >
        <OrganicMiniIcon kind="cryo" size={42} />
        <View style={{ flex: 1 }}>
          <CardTitleText>
            {hasSpecimens
              ? t(
                  totalSpecimens === 1
                    ? "care.cryo_vault.overview.title_count_one"
                    : "care.cryo_vault.overview.title_count_other",
                  { params: { count: String(totalSpecimens) } },
                )
              : t("care.cryo_vault.overview.title_empty")}
          </CardTitleText>
          <CardSubtitleText>
            {hasSpecimens
              ? t(
                  facilityCount === 1
                    ? "care.cryo_vault.overview.subtitle_one_facility"
                    : "care.cryo_vault.overview.subtitle_other_facility",
                  { params: { count: String(facilityCount) } },
                )
              : t("care.cryo_vault.overview.subtitle_empty")}
          </CardSubtitleText>
        </View>
        <ChevronRight size={15} color={TEXT_LIGHT} strokeWidth={1.8} />
      </Pressable>

      {carouselItems.length > 0 ? (
        <HorizontalSnapCarousel
          items={carouselItems}
          gap={9}
          fallbackCardWidth={198}
          getCardWidth={(containerWidth) =>
            Math.min(218, Math.max(184, containerWidth * 0.48))
          }
          keyExtractor={(item) => item.specimen.id}
          measureKey={carouselItems.map((item) => item.specimen.id).join("|")}
          renderItem={(item, width, _index, equalizedHeight) => (
            <CryoSpecimenCarouselCard
              specimen={item.specimen}
              width={width}
              equalizedHeight={equalizedHeight}
              onPress={
                onPressSpecimen ? () => onPressSpecimen(item.specimen) : onPress
              }
            />
          )}
        />
      ) : (
        <CryoVaultEmptyState
          onAddSpecimen={onAddSpecimen}
          onNoSpecimens={onNoSpecimens}
        />
      )}
    </CardSurface>
  );
}

// Hook for callers that need to build snapshots from storage groups
// while staying inside the React render flow (uses translations + locale).
export function useCryoSpecimenSnapshotsBuilder() {
  const t = useTranslations();
  const { locale } = useLocale();
  const bcp47 = locale === "fr" ? "fr-FR" : "en-US";
  return (
    storageGroups: ReturnType<typeof useFrozenSpecimens>["storageGroups"],
    cohorts: readonly OocyteCohort[] = [],
  ) =>
    buildCryoSpecimenSnapshotsFromStorageGroups(
      t,
      bcp47,
      storageGroups,
      cohorts,
    );
}
